import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { setDocumentTitle } from '../../utils/documentTitle';
import AuctionCard from './AuctionCard';
import Pagination from './Pagination';
import BottomNavigation from '../Layout/BottomNavigation';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';

const AuctionsList = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { placeBid } = useMarketplace();
  const [auctions, setAuctions] = useState([]);
  const [filteredAuctions, setFilteredAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // all, active, ended, myAuctions
  const [sortBy, setSortBy] = useState('ending_soon'); // ending_soon, newest, highest_price, most_bids
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Adjust itemsPerPage based on view mode
  useEffect(() => {
    if (viewMode === 'grid') {
      setItemsPerPage(12); // 12 cards per page for grid
    } else if (viewMode === 'list') {
      setItemsPerPage(10); // 10 cards per page for list (more space per card)
    } else if (viewMode === 'compact') {
      setItemsPerPage(20); // 20 cards per page for compact (smaller cards)
    }
    setCurrentPage(1); // Reset to first page when changing view
  }, [viewMode]);

  // Categories
  const categories = [
    { id: 'all', name: t('marketplace.all'), icon: '🔨' },
    { id: 'Flowers', name: t('marketplace.flowers'), icon: '🌿' },
    { id: 'Seeds', name: t('marketplace.seeds'), icon: '🌱' },
    { id: 'Edibles', name: t('marketplace.edibles'), icon: '🍪' },
    { id: 'Pre Rolls', name: t('marketplace.preRolls'), icon: '🚬' },
    { id: 'Cannabis Oils', name: t('marketplace.cannabisOils'), icon: '💧' },
    { id: 'CBD Products', name: t('marketplace.cbdProducts'), icon: '🌿' }
  ];

  // Status filters
  const statusFilters = [
    { value: 'all', label: 'ทั้งหมด', icon: '📋' },
    { value: 'active', label: 'กำลังดำเนินการ', icon: '🔥' },
    { value: 'ending_soon', label: 'ใกล้หมดเวลา', icon: '⏰' },
    { value: 'ended', label: 'หมดเวลา', icon: '⏸️' },
    { value: 'myAuctions', label: 'ประมูลของฉัน', icon: '👤' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'ending_soon', label: 'ใกล้หมดเวลาก่อน', icon: '⏰' },
    { value: 'newest', label: 'ใหม่ล่าสุด', icon: '🆕' },
    { value: 'highest_price', label: 'ราคาสูงสุด', icon: '💰' },
    { value: 'most_bids', label: 'ผู้เสนอมากที่สุด', icon: '👥' }
  ];

  useEffect(() => {
    setDocumentTitle('ประมูลทั้งหมด - CannaTrade');
    fetchAuctions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [auctions, searchTerm, selectedCategory, statusFilter, sortBy]);

  // Adjust itemsPerPage based on view mode
  useEffect(() => {
    if (viewMode === 'grid') {
      setItemsPerPage(12); // 12 cards per page for grid
    } else if (viewMode === 'list') {
      setItemsPerPage(10); // 10 cards per page for list (more space per card)
    } else if (viewMode === 'compact') {
      setItemsPerPage(20); // 20 cards per page for compact (smaller cards)
    }
    setCurrentPage(1); // Reset to first page when changing view
  }, [viewMode]);

  const fetchAuctions = async () => {
    setLoading(true);
    try {
      let auctionsQuery;
      
      // Query based on status filter
      if (statusFilter === 'myAuctions' && currentUser) {
        auctionsQuery = query(
          collection(db, 'auctions'),
          where('sellerId', '==', currentUser.uid),
          limit(100)
        );
      } else if (statusFilter === 'active') {
        try {
          auctionsQuery = query(
            collection(db, 'auctions'),
            where('status', '==', 'active'),
            limit(100)
          );
        } catch (error) {
          auctionsQuery = query(
            collection(db, 'auctions'),
            limit(100)
          );
        }
      } else {
        auctionsQuery = query(
          collection(db, 'auctions'),
          limit(100)
        );
      }

      const snapshot = await getDocs(auctionsQuery);
      let auctionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load product images for auctions that don't have images
      const auctionsWithImages = await Promise.all(
        auctionsData.map(async (auction) => {
          // If auction already has image, return as is
          if (auction.productImage || auction.productImages?.[0] || auction.images?.[0]) {
            return auction;
          }

          // Try to load from product
          if (auction.productId) {
            try {
              const productRef = doc(db, 'products', auction.productId);
              const productSnap = await getDoc(productRef);
              if (productSnap.exists()) {
                const productData = productSnap.data();
                if (productData.images && productData.images.length > 0) {
                  return {
                    ...auction,
                    productImage: productData.images[0],
                    productImages: productData.images
                  };
                }
              }
            } catch (error) {
              console.error('Error loading product image for auction:', error);
            }
          }

          return auction;
        })
      );

      setAuctions(auctionsWithImages);
    } catch (error) {
      console.error('Error fetching auctions:', error);
      setAuctions([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...auctions];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(auction => 
        auction.productName?.toLowerCase().includes(searchLower) ||
        auction.productId?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter (if product has category)
    if (selectedCategory !== 'all') {
      // Note: This would require fetching product data, simplified for now
      // filtered = filtered.filter(auction => auction.productCategory === selectedCategory);
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        return auction.status === 'active' && 
               !auction.isEnded && 
               endTime && 
               endTime > new Date();
      });
    } else if (statusFilter === 'ending_soon') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        if (!endTime) return false;
        const hoursLeft = (endTime.getTime() - new Date().getTime()) / (1000 * 60 * 60);
        return auction.status === 'active' && 
               !auction.isEnded && 
               hoursLeft > 0 && 
               hoursLeft <= 24;
      });
    } else if (statusFilter === 'ended') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        return auction.isEnded || 
               (endTime && endTime <= new Date()) ||
               auction.status === 'ended';
      });
    } else if (statusFilter === 'myAuctions') {
      if (currentUser) {
        filtered = filtered.filter(auction => auction.sellerId === currentUser.uid);
      } else {
        filtered = [];
      }
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'ending_soon') {
        const aEnd = getEndTime(a);
        const bEnd = getEndTime(b);
        if (!aEnd) return 1;
        if (!bEnd) return -1;
        return aEnd.getTime() - bEnd.getTime();
      } else if (sortBy === 'newest') {
        const aCreated = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const bCreated = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return bCreated.getTime() - aCreated.getTime();
      } else if (sortBy === 'highest_price') {
        return (b.currentBid || b.startingPrice || 0) - (a.currentBid || a.startingPrice || 0);
      } else if (sortBy === 'most_bids') {
        return (b.bids?.length || 0) - (a.bids?.length || 0);
      }
      return 0;
    });

    setFilteredAuctions(filtered);
    setCurrentPage(1);
  };

  const getEndTime = (auction) => {
    if (!auction.endTime) return null;
    if (auction.endTime.toDate && typeof auction.endTime.toDate === 'function') {
      return auction.endTime.toDate();
    }
    if (auction.endTime instanceof Date) {
      return auction.endTime;
    }
    const parsed = new Date(auction.endTime);
    return !isNaN(parsed.getTime()) ? parsed : null;
  };

  // Pagination
  const totalPages = Math.ceil(filteredAuctions.length / itemsPerPage);
  const paginatedAuctions = filteredAuctions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const activeAuctions = auctions.filter(a => {
    const endTime = getEndTime(a);
    return a.status === 'active' && !a.isEnded && endTime && endTime > new Date();
  }).length;
  const endedAuctions = auctions.filter(a => {
    const endTime = getEndTime(a);
    return a.isEnded || (endTime && endTime <= new Date()) || a.status === 'ended';
  }).length;
  const myAuctionsCount = currentUser 
    ? auctions.filter(a => a.sellerId === currentUser.uid).length 
    : 0;
  const totalBids = auctions.reduce((sum, a) => sum + (a.bids?.length || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดประมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">🔨 ประมูลทั้งหมด</h1>
          <p className="text-sm md:text-base text-gray-600">
            ค้นหาและเสนอราคาประมูลสินค้าคุณภาพ
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-yellow-500">
            <div className="text-2xl md:text-3xl font-bold text-yellow-600">{activeAuctions}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">กำลังดำเนินการ</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-red-500">
            <div className="text-2xl md:text-3xl font-bold text-red-600">{endedAuctions}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">หมดเวลา</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-blue-500">
            <div className="text-2xl md:text-3xl font-bold text-blue-600">{totalBids}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">การเสนอราคา</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-green-500">
            <div className="text-2xl md:text-3xl font-bold text-green-600">{myAuctionsCount}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">ประมูลของฉัน</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหาประมูล..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 md:py-3 pl-10 md:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {statusFilters.map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    statusFilter === filter.value
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{filter.icon}</span>
                  <span className="text-sm md:text-base">{filter.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.icon}</span>
                  <span className="text-sm md:text-base">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort and View Mode */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Sort Options */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm md:text-base text-gray-600">เรียงตาม:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {sortOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      sortBy === option.value
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm md:text-base text-gray-600">มุมมอง:</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-yellow-500 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Grid View"
                >
                  <span className="text-base">⊞</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    viewMode === 'list'
                      ? 'bg-yellow-500 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="List View"
                >
                  <span className="text-base">☰</span>
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    viewMode === 'compact'
                      ? 'bg-yellow-500 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Compact View"
                >
                  <span className="text-base">☷</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm md:text-base text-gray-600">
          พบ {filteredAuctions.length} ประมูล
        </div>

        {/* Auctions Display */}
        {paginatedAuctions.length > 0 ? (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3 mb-6">
                {paginatedAuctions.map(auction => (
                  <AuctionCard
                    key={auction.id}
                    auction={auction}
                    onPlaceBid={placeBid}
                  />
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-3 mb-6">
                {paginatedAuctions.map(auction => {
                  const endTime = auction.endTime?.toDate?.() || new Date(auction.endTime);
                  const timeRemaining = endTime 
                    ? Math.max(0, Math.floor((endTime.getTime() - new Date().getTime()) / 1000 / 60 / 60))
                    : null;
                  const isMyAuction = currentUser?.uid === auction.sellerId;
                  const productImage = auction.productImage || auction.productImages?.[0] || auction.images?.[0];

                  return (
                    <Link key={auction.id} to={`/marketplace/auctions/${auction.id}`} className="block">
                      <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 hover:shadow-md transition-shadow border border-yellow-200">
                        <div className="flex items-center gap-3 md:gap-4">
                          {/* Image */}
                          <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-400 to-yellow-600">
                            {productImage ? (
                              <img
                                src={productImage}
                                alt={auction.productName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<img src="/logo.png" alt="Logo" class="w-full h-full object-contain opacity-80" onerror="this.style.display=\'none\'; this.parentElement.innerHTML=\'<div class=\'text-white text-xl\'>🔨</div>\';" />';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <img 
                                  src="/logo.png" 
                                  alt="CannaTrade Logo" 
                                  className="w-12 h-12 object-contain opacity-80"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="text-white text-xl">🔨</div>';
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm md:text-base font-medium text-gray-800 mb-1 line-clamp-1">
                                  {auction.productName}
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                                    🔨 ประมูล
                                  </span>
                                  {timeRemaining !== null && timeRemaining > 0 && (
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                      timeRemaining <= 0 
                                        ? 'bg-red-500 text-white' 
                                        : timeRemaining < 24 
                                          ? 'bg-orange-500 text-white' 
                                          : 'bg-green-500 text-white'
                                    }`}>
                                      {timeRemaining < 24 ? `${timeRemaining} ชม.` : `${Math.floor(timeRemaining / 24)} วัน`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs md:text-sm">
                              <div>
                                <span className="text-gray-500">ราคาปัจจุบัน:</span>
                                <span className="ml-1 font-bold text-red-500">
                                  ฿{auction.currentBid?.toLocaleString() || auction.startingPrice?.toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">เริ่มต้น:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  ฿{auction.startingPrice?.toLocaleString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">ผู้เสนอ:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  {auction.bids?.length || 0} คน
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex-shrink-0">
                            {!auction.isEnded && auction.status === 'active' && !isMyAuction && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = `/marketplace/auctions/${auction.id}`;
                                }}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors whitespace-nowrap"
                              >
                                เสนอราคา
                              </button>
                            )}
                            {!auction.isEnded && auction.status === 'active' && isMyAuction && (
                              <div className="bg-gray-100 text-gray-600 font-medium py-2 px-4 rounded-lg text-sm text-center whitespace-nowrap">
                                ประมูลของฉัน
                              </div>
                            )}
                            {auction.isEnded && (
                              <div className="bg-gray-300 text-gray-600 font-medium py-2 px-4 rounded-lg text-sm text-center whitespace-nowrap">
                                หมดเวลา
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Compact View */}
            {viewMode === 'compact' && (
              <div className="space-y-2 mb-6">
                {paginatedAuctions.map(auction => {
                  const endTime = auction.endTime?.toDate?.() || new Date(auction.endTime);
                  const timeRemaining = endTime 
                    ? Math.max(0, Math.floor((endTime.getTime() - new Date().getTime()) / 1000 / 60 / 60))
                    : null;
                  const isMyAuction = currentUser?.uid === auction.sellerId;
                  const productImage = auction.productImage || auction.productImages?.[0] || auction.images?.[0];

                  return (
                    <Link key={auction.id} to={`/marketplace/auctions/${auction.id}`} className="block">
                      <div className="bg-white rounded-lg shadow-sm p-2 md:p-3 hover:shadow-md transition-shadow border border-yellow-200">
                        <div className="flex items-center gap-3">
                          {/* Image */}
                          <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-400 to-yellow-600">
                            {productImage ? (
                              <img
                                src={productImage}
                                alt={auction.productName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<img src="/logo.png" alt="Logo" class="w-full h-full object-contain opacity-80" onerror="this.style.display=\'none\'; this.parentElement.innerHTML=\'<div class=\'text-white text-xl\'>🔨</div>\';" />';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <img 
                                  src="/logo.png" 
                                  alt="CannaTrade Logo" 
                                  className="w-10 h-10 object-contain opacity-80"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="text-white text-xl">🔨</div>';
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm md:text-base font-medium text-gray-800 truncate mb-1">
                              {auction.productName}
                            </h3>
                            <div className="flex items-center gap-2 text-xs md:text-sm">
                              <span className="font-bold text-red-500">
                                ฿{auction.currentBid?.toLocaleString() || auction.startingPrice?.toLocaleString()}
                              </span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">
                                {auction.bids?.length || 0} ผู้เสนอ
                              </span>
                              {timeRemaining !== null && timeRemaining > 0 && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className={`${
                                    timeRemaining <= 0 
                                      ? 'text-red-500' 
                                      : timeRemaining < 24 
                                        ? 'text-orange-500' 
                                        : 'text-green-500'
                                  }`}>
                                    {timeRemaining < 24 ? `${timeRemaining}ชม.` : `${Math.floor(timeRemaining / 24)}วัน`}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex-shrink-0">
                            {!auction.isEnded && auction.status === 'active' && !isMyAuction && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = `/marketplace/auctions/${auction.id}`;
                                }}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-1.5 px-3 rounded-lg text-xs md:text-sm transition-colors whitespace-nowrap"
                              >
                                เสนอราคา
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 mb-20 md:mb-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredAuctions.length}
                  onItemsPerPageChange={(newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-20 md:mb-6">
            <div className="text-6xl mb-4">🔨</div>
            <p className="text-lg md:text-xl text-gray-800 mb-2">ไม่พบประมูล</p>
            <p className="text-sm md:text-base text-gray-600">
              ลองเปลี่ยนคำค้นหาหรือตัวกรองดูนะครับ
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation />
    </div>
  );
};

export default AuctionsList;

